<template>
  <v-container>
    <!-- Search Area -->
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="text-h3 mb-6">UV Index Query</h1>
      </v-col>
      <v-col cols="12" sm="8" md="6" class="mx-auto">
        <v-card>
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12" sm="8">
                <v-autocomplete
                  v-model="searchLocation"
                  :items="cities"
                  :loading="isSearching"
                  label="Search City"
                  placeholder="Enter city name, e.g: mel"
                  variant="outlined"
                  hide-details
                  clearable
                  item-title="name"
                  item-value="id"
                  return-object
                  :filter="customFilter"
                  height="56"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-map-marker</v-icon>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="4" class="d-flex">
                <v-btn
                  color="primary"
                  class="ml-sm-3 mt-3 mt-sm-0"
                  block
                  :loading="loading"
                  :disabled="!searchLocation"
                  @click="searchUVIndex"
                  height="56"
                  style="margin-top: 0 !important"
                >
                  <v-icon start>mdi-magnify</v-icon>
                  Go
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- UV Index Card -->
      <v-col cols="12" md="6">
        <v-card class="mx-auto weather-card" elevation="2">
          <v-card-title class="text-h5 pa-4 d-flex align-center">
            <v-icon color="warning" size="32" class="mr-3"
              >mdi-white-balance-sunny</v-icon
            >
            UV Index
            <v-chip :color="uvIndexColor" class="ml-auto" size="large" label>
              {{ uvIndexLevel }}
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-4 text-center">
            <div
              class="text-h1 font-weight-bold"
              :class="'text-' + uvIndexColor"
            >
              {{ uvIndex }}
            </div>
            <div class="text-subtitle-1 text-grey mt-2" v-if="currentLocation">
              {{ currentLocation }}
            </div>

            <v-divider class="my-4"></v-divider>
            <div class="text-body-1">
              {{ uvIndexDescription }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Weather Conditions Card -->
      <v-col cols="12" md="6">
        <v-card class="mx-auto weather-card" elevation="2">
          <v-card-title class="text-h5 pa-4 d-flex align-center">
            <v-icon :color="getWeatherTipColor" size="32" class="mr-3"
              >mdi-weather-partly-cloudy</v-icon
            >
            Weather Conditions
            <v-chip
              :color="getWeatherTipColor"
              class="ml-auto"
              size="large"
              label
            >
              {{ weatherDescription }}
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-4 text-center">
            <div
              class="text-h1 font-weight-bold"
              :class="'text-' + getWeatherTipColor"
            >
              {{ temperature }}°
            </div>
            <v-divider class="my-4"></v-divider>
            <div class="d-flex justify-space-around">
              <div>
                <v-icon size="32">mdi-water-percent</v-icon>
                <div>{{ humidity }}%</div>
              </div>
              <div>
                <v-icon size="32">mdi-weather-windy</v-icon>
                <div>{{ windSpeed }} km/h</div>
              </div>
            </div>

            <v-divider class="mb-6"></v-divider>

            <div class="weather-tips">
              <div class="d-flex align-center mb-3">
                <v-icon :color="getWeatherTipColor" size="24" class="mr-3">{{
                  getWeatherTipIcon
                }}</v-icon>
                <div class="text-body-1">{{ getWeatherTip }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Sunscreen Tips Card -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card class="mx-auto tips-card" elevation="2">
          <v-card-title class="text-h5 d-flex align-center pa-6">
            <v-icon color="warning" size="32" class="mr-3">mdi-sun-clock</v-icon>
            Sun Protection Tips
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="tips-row">
              <!-- Basic Sun Protection Knowledge -->
              <div class="tip-card">
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon color="primary" size="32" class="tip-icon">mdi-book-open-variant</v-icon>
                  </template>
                  <v-card-title class="tip-title pb-4">Basic Knowledge</v-card-title>
                  <v-card-text class="tip-content">
                    <ul class="tip-list">
                      <li>Higher SPF provides better protection</li>
                      <li>Apply sunscreen 15-30 minutes before exposure</li>
                      <li>Reapply every 2-3 hours</li>
                      <li>Protection needed even on cloudy days</li>
                    </ul>
                  </v-card-text>
                </v-card-item>
              </div>

              <!-- Sunscreen Product Selection -->
              <div class="tip-card">
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon color="success" size="32" class="tip-icon">mdi-bottle-tonic-plus</v-icon>
                  </template>
                  <v-card-title class="tip-title pb-4">Product Selection</v-card-title>
                  <v-card-text class="tip-content">
                    <ul class="tip-list">
                      <li>Choose SPF30+ sunscreen</li>
                      <li>Oily skin: Lightweight formula</li>
                      <li>Dry skin: Moisturizing formula</li>
                      <li>Sensitive skin: Physical sunscreen</li>
                    </ul>
                  </v-card-text>
                </v-card-item>
              </div>

              <!-- Usage Guidelines -->
              <div class="tip-card">
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon color="warning" size="32" class="tip-icon">mdi-alert-circle</v-icon>
                  </template>
                  <v-card-title class="tip-title pb-4">Usage Guidelines</v-card-title>
                  <v-card-text class="tip-content">
                    <ul class="tip-list">
                      <li>Apply evenly on exposed areas</li>
                      <li>Use sufficient amount (1oz/30ml)</li>
                      <li>Reapply after swimming/sweating</li>
                      <li>Set reminders for reapplication</li>
                    </ul>
                  </v-card-text>
                </v-card-item>
              </div>

              <!-- Common Myths -->
              <div class="tip-card">
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon color="error" size="32" class="tip-icon">mdi-close-circle</v-icon>
                  </template>
                  <v-card-title class="tip-title pb-4">Common Myths</v-card-title>
                  <v-card-text class="tip-content">
                    <ul class="tip-list">
                      <li>No need to reapply sunscreen</li>
                      <li>Cloudy days don't need protection</li>
                      <li>Sunscreen damages your skin</li>
                      <li>Higher SPF is always better</li>
                    </ul>
                  </v-card-text>
                </v-card-item>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error Message -->
    <v-snackbar
      v-model="showError"
      :color="error ? 'error' : 'success'"
      :timeout="3000"
    >
      {{ error || successMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

// List of Australian cities with their regions
const cities = [
  { id: "sydney", name: "Sydney", region: "New South Wales" },
  { id: "melbourne", name: "Melbourne", region: "Victoria" },
  { id: "brisbane", name: "Brisbane", region: "Queensland" },
  { id: "perth", name: "Perth", region: "Western Australia" },
  { id: "adelaide", name: "Adelaide", region: "South Australia" },
  { id: "goldcoast", name: "Gold Coast", region: "Queensland" },
  { id: "canberra", name: "Canberra", region: "Australian Capital Territory" },
  { id: "hobart", name: "Hobart", region: "Tasmania" },
  { id: "darwin", name: "Darwin", region: "Northern Territory" },
  { id: "newcastle", name: "Newcastle", region: "New South Wales" },
];

export default {
  name: "UVIndexView",
  data() {
    return {
      searchLocation: { id: "melbourne", name: "Melbourne", region: "Victoria" }, // Default city
      cities,
      currentLocation: "",
      loading: false,
      isSearching: false,
      uvIndex: "--",
      temperature: "--",
      humidity: "--",
      windSpeed: "--",
      weatherDescription: "Loading...",
      weatherIcon: "mdi-weather-sunny",
      error: null,
      successMessage: "",
      showError: false,
    };
  },
  computed: {
    // Calculate UV index level based on the current UV index value
    uvIndexLevel() {
      if (this.uvIndex === "--") return "Unknown";
      const index = Number(this.uvIndex);
      if (index <= 2) return "Low";
      if (index <= 5) return "Moderate";
      if (index <= 7) return "High";
      if (index <= 10) return "Very High";
      return "Extreme";
    },
    // Determine color based on UV index level
    uvIndexColor() {
      if (this.uvIndex === "--") return "grey";
      const index = Number(this.uvIndex);
      if (index <= 2) return "success";
      if (index <= 5) return "warning";
      if (index <= 7) return "orange-darken-2";
      if (index <= 10) return "error";
      return "deep-purple";
    },
    // Get description based on UV index level
    uvIndexDescription() {
      if (this.uvIndex === "--") return "Fetching UV index...";
      const index = Number(this.uvIndex);
      if (index <= 2)
        return "No protection needed, safe for outdoor activities";
      if (index <= 5) return "Basic protection required, use SPF15+ sunscreen";
      if (index <= 7)
        return "Active protection needed, use SPF30+ sunscreen and wear a hat";
      if (index <= 10)
        return "Extra protection needed, use SPF50+ sunscreen, avoid prolonged outdoor activities";
      return "Full protection required, avoid going outside if possible";
    },
    // Get weather tip based on temperature
    getWeatherTip() {
      const temp = Number(this.temperature);
      if (temp >= 35) return "High temperature alert, stay cool and hydrated";
      if (temp >= 30) return "Hot weather, remember sun protection";
      if (temp >= 20) return "Pleasant weather, perfect for outdoor activities";
      if (temp >= 10) return "Comfortable temperature, dress accordingly";
      return "Cold weather, stay warm";
    },
    // Get color for weather tip based on temperature
    getWeatherTipColor() {
      const temp = Number(this.temperature);
      if (temp >= 35) return "error";
      if (temp >= 30) return "warning";
      if (temp >= 20) return "success";
      if (temp >= 10) return "info";
      return "primary";
    },
    // Get weather icon based on temperature
    getWeatherTipIcon() {
      const temp = Number(this.temperature);
      if (temp >= 35) return "mdi-sun-thermometer";
      if (temp >= 30) return "mdi-sun-wireless";
      if (temp >= 20) return "mdi-weather-sunny";
      if (temp >= 10) return "mdi-weather-partly-cloudy";
      return "mdi-snowflake";
    },
  },
  methods: {
    // Custom filter for city search
    customFilter(item, query) {
      if (!query) return true;

      const searchQuery = query.toLowerCase().trim();
      const cityName = item.name.toLowerCase();
      const regionName = item.region.toLowerCase();

      // Check if city name contains search term
      const matchesCity = cityName.includes(searchQuery);
      // Check if region name contains search term
      const matchesRegion = regionName.includes(searchQuery);

      return matchesCity || matchesRegion;
    },
    // Search UV index for selected city
    async searchUVIndex() {
      if (!this.searchLocation) return;

      this.loading = true;
      try {
        // Get coordinates
        const geoResponse = await axios.post(
          "https://toio8d3rdi.execute-api.ap-southeast-2.amazonaws.com/getGeoCoordinate",
          { city: this.searchLocation.name }
        );

        if (geoResponse.data.lat && geoResponse.data.lon) {
          // Get UV Index + Weather data
          const uvResponse = await axios.post(
            "https://fl33slzg14.execute-api.ap-southeast-2.amazonaws.com/getUVIndex",
            {
              lat: geoResponse.data.lat,
              lon: geoResponse.data.lon,
            }
          );

          this.updateUVData(uvResponse.data);
          this.showSuccess(`Updated UV index for ${this.searchLocation.name}`);
        } else {
          this.error = "Failed to get coordinates";
          this.showError = true;
        }
      } catch (error) {
        console.error("Error fetching UV index:", error);
        this.error = "Failed to fetch UV index, please try again";
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
    // Update UV and weather data
    updateUVData(data) {
      this.currentLocation = data.location || "";
      this.uvIndex = data.uvIndex !== undefined ? data.uvIndex : null;
      this.uvIndex = data.uvIndex;
      this.temperature = data.temperature;
      this.humidity = data.humidity;
      this.windSpeed = data.windSpeed;
      this.weatherDescription = data.weatherDescription;
      this.weatherIcon = this.getWeatherIcon(data.weatherCode);
    },
    // Get weather icon based on weather code
    getWeatherIcon(code) {
      const iconMap = {
        clear: "mdi-weather-sunny",
        cloudy: "mdi-weather-cloudy",
        rain: "mdi-weather-rainy",
        storm: "mdi-weather-lightning-rainy",
        snow: "mdi-weather-snowy",
      };
      return iconMap[code] || "mdi-weather-sunny";
    },
    // Show success message
    showSuccess(message) {
      this.error = null;
      this.successMessage = message;
      this.showError = true;
    },
    // Handle successful location retrieval
    async handleLocationSuccess(position) {
      try {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        
        // Get UV Index and weather data
        await this.fetchWeatherData();
      } catch (error) {
        console.error('Error in handleLocationSuccess:', error);
      }
    },
  },
  async mounted() {
    // Auto-search Melbourne data when component is mounted
    await this.searchUVIndex();
  },
};
</script>

<style scoped>
.weather-card {
  border-radius: 16px;
  transition: transform 0.3s, box-shadow 0.3s;
  overflow: hidden;
  background: linear-gradient(to bottom right, #ffffff, #f8f9fa);
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}

.weather-card .v-card-title {
  flex-shrink: 0;
  padding: 16px !important;
}

.weather-card .v-card-text {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px !important;
}

.weather-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.text-h1 {
  font-size: 3.5rem !important;
  line-height: 1.2;
}

.text-h4 {
  font-size: 1.75rem !important;
  line-height: 1.2;
}

.mb-6 {
  margin-bottom: 16px !important;
}

.v-chip {
  font-weight: 600;
}

.weather-tips {
  background: rgba(0, 0, 0, 0.02);
  padding: 12px;
  border-radius: 8px;
  margin-top: auto;
}

.v-divider {
  opacity: 0.1;
}

.h-100 {
  height: 100%;
}

.text-grey {
  color: rgba(0, 0, 0, 0.6);
}

.v-card-text {
  font-size: 1.1rem;
}

.v-btn {
  text-transform: none;
  font-size: 16px;
}

.v-autocomplete {
  width: 100%;
}

@media (min-width: 600px) {
  .v-btn {
    margin-top: 0;
  }
}

.tips-card {
  border-radius: 16px;
  background: linear-gradient(to bottom right, #ffffff, #f8f9fa);
}

.tips-row {
  gap: 24px;
  display: flex;
  flex-wrap: wrap;
  margin: 0 -12px;
}

.tip-card {
  height: 320px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 20px;
  display: flex;
  flex-direction: column;
  margin: 0 0 24px;
  width: calc(50% - 24px);
}

.tip-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.12);
}

.tip-icon {
  margin-bottom: 16px;
  font-size: 32px !important;
}

.tip-title {
  font-size: 1.3rem !important;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
}

.tip-content {
  padding: 0 !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.tip-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.tip-list li {
  padding: 8px 0;
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.tip-list li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

@media (max-width: 959px) {
  .tips-row {
    gap: 16px;
  }
  
  .tip-card {
    width: 100%;
    margin-bottom: 16px;
    height: auto;
    min-height: 300px;
  }
  
  .tip-card:last-child {
    margin-bottom: 0;
  }
}
</style>
